import { ChevronRight } from "lucide-react";

export type InternalLink = { href: string; label: string };

/**
 * Compact, crawlable internal-link block. Used to keep standalone pages
 * (contact, about, pricing, promos, guides) from becoming link sinks.
 */
export function InternalLinkRow({
  title,
  eyebrow,
  links,
  tone = "light",
}: {
  title: string;
  eyebrow?: string;
  links: InternalLink[];
  tone?: "light" | "white";
}) {
  if (links.length === 0) return null;
  return (
    <section
      className={
        tone === "white"
          ? "bg-white py-12 md:py-14 border-t border-gray-200"
          : "bg-[var(--brand-light-gray)] py-12 md:py-14 border-t border-gray-200"
      }
    >
      <div className="mx-auto max-w-[1100px] px-4">
        <div className="text-center mb-6">
          {eyebrow && (
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {eyebrow}
            </p>
          )}
          <h2
            className="text-[var(--brand-deep-blue)] text-2xl md:text-3xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            {title}
          </h2>
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                className={`group flex items-center justify-between gap-2 rounded-md border border-[var(--brand-deep-blue)]/10 p-4 transition-all hover:border-[var(--brand-bright-blue)] hover:shadow-md ${
                  tone === "white" ? "bg-[var(--brand-light-gray)]" : "bg-white"
                }`}
              >
                <span className="text-sm font-semibold leading-tight text-[var(--brand-charcoal)] transition-colors group-hover:text-[var(--brand-bright-blue)]">
                  {l.label}
                </span>
                <ChevronRight
                  size={16}
                  className="shrink-0 text-[var(--brand-charcoal)]/40"
                />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** Popular service pages — safe default set for generic pages. */
export const POPULAR_SERVICE_LINKS: InternalLink[] = [
  { href: "/house-washing", label: "House Washing" },
  { href: "/roof-washing", label: "Roof Washing & Soft Wash" },
  { href: "/gutter-cleaning", label: "Gutter Cleaning" },
  { href: "/gutter-guards", label: "RainDrop Gutter Guards" },
  { href: "/concrete-cleaning-sealing", label: "Concrete Cleaning & Sealing" },
  { href: "/paver-cleaning-sealing", label: "Paver Cleaning & Sealing" },
  { href: "/deck-patio-cleaning", label: "Deck & Patio Cleaning" },
  { href: "/window-cleaning", label: "Window Cleaning" },
  { href: "/commercial-pressure-washing", label: "Commercial Pressure Washing" },
];

/** Core city landing pages. */
export const CORE_CITY_LINKS: InternalLink[] = [
  { href: "/pressure-washing-albany-ny", label: "Pressure Washing in Albany, NY" },
  { href: "/pressure-washing-schenectady-ny", label: "Pressure Washing in Schenectady, NY" },
  { href: "/pressure-washing-troy-ny", label: "Pressure Washing in Troy, NY" },
  { href: "/pressure-washing-clifton-park-ny", label: "Pressure Washing in Clifton Park, NY" },
  { href: "/pressure-washing-saratoga-springs-ny", label: "Pressure Washing in Saratoga Springs, NY" },
  { href: "/service-area", label: "Full Capital Region Service Area" },
];
